'use client';

import { Element, ElementProps } from '@vuddy/components';
import { isNonNil, jsonAssignInlineVars } from '@vuddy/utils';
import { isNil } from 'lodash-es';
import {
  Children,
  ComponentProps,
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Slider from 'react-slick';
import * as styles from './carousel.css';

type SliderProps = ComponentProps<typeof Slider>;

export type CarouselProps = SliderProps &
  Pick<ElementProps, 'padding' | 'paddingY' | 'paddingX'> & {
    children: ReactNode;
    onChange?: (index: number) => void;
    showIndicator?: boolean;
    indicatorPosition?: {
      top?: string | number;
      bottom?: string | number;
      left?: string | number;
      right?: string | number;
    };
  };

export const Carousel = forwardRef<any, CarouselProps>(
  (
    {
      children,
      padding,
      paddingX,
      paddingY,
      onChange,
      customPaging,
      showIndicator,
      indicatorPosition,
      ...slideProps
    },
    ref
  ) => {
    const internalRef = useRef<HTMLDivElement | null>(null);
    const positionRef = useRef<number | null>(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const childrenArray = Children.toArray(children);
    const sliderRef = useRef<Slider>(null);

    // ref를 통해 외부에서 접근할 수 있는 메서드들 노출
    useImperativeHandle(ref, () => ({
      slickPause: () => sliderRef.current?.slickPause(),
      slickPlay: () => sliderRef.current?.slickPlay(),
      slickNext: () => sliderRef.current?.slickNext(),
      slickPrev: () => sliderRef.current?.slickPrev(),
    }));

    const {
      slidesToShow: slidesToShowProp,
      slidesToScroll: slidesToScrollProp,
      ...restSlideProps
    } = slideProps;

    const settings: SliderProps = {
      dots: isNonNil(slideProps.appendDots),
      infinite: childrenArray.length > 1,
      slidesToShow: slidesToShowProp ?? 1,
      slidesToScroll: slidesToScrollProp ?? 1,
      arrows: false,
      centerMode: false,
      autoplay: false,
      autoplaySpeed: 8000,
      speed: 800,
      pauseOnDotsHover: true,
      touchMove: true,
      swipe: childrenArray.length > 1,
      dotsClass: 'slick-pagination-bar',
      customPaging,
      beforeChange: (_, newIndex) => {
        setCurrentSlide(newIndex);
        onChange?.(newIndex);
      },
      ...restSlideProps, // 사용자 설정이 나중에 오므로 centerMode, centerPadding 등을 덮어쓸 수 있음
    };

    useEffect(() => {
      const wrapper = internalRef.current;

      if (isNil(wrapper)) return;

      const handleMousedown = (e: MouseEvent) => {
        positionRef.current = e.clientX;
      };

      const handleMouseup = (e: MouseEvent) => {
        if (positionRef.current !== e.clientX) {
          e.stopPropagation();
          e.preventDefault();
        } else {
          positionRef.current = null;
        }
      };

      wrapper.addEventListener('mousedown', handleMousedown);
      wrapper.addEventListener('click', handleMouseup);

      return () => {
        wrapper.removeEventListener('mousedown', handleMousedown);
        wrapper.removeEventListener('click', handleMouseup);
      };
    }, []);

    return (
      <div
        ref={internalRef}
        className={styles.wrapper}
        style={jsonAssignInlineVars({
          [styles.paddingLeftVar]: `${paddingX ?? padding ?? 16}px`,
          [styles.paddingRightVar]: `${paddingX ?? padding ?? 16}px`,
          [styles.indicatorTop]: indicatorPosition?.top ?? 'undefined',
          [styles.indicatorBottom]: indicatorPosition?.bottom ?? 'undefined',
          [styles.indicatorLeft]: indicatorPosition?.left ?? 'undefined',
          [styles.indicatorRight]: indicatorPosition?.right ?? 'undefined',
        })}>
        <Slider ref={sliderRef} {...settings}>
          {childrenArray.map((child, index) => (
            <div key={index}>{child}</div>
          ))}
        </Slider>
        {showIndicator && childrenArray.length > 1 && (
          <Element className={styles.indicator} typography="captionXs">
            {currentSlide + 1}/{childrenArray.length}
          </Element>
        )}
      </div>
    );
  }
);
